<template>
  <div class="condolence-view">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <h2>부조금 관리</h2>
      <div v-if="userStore.isCondolenceManager || userStore.profile?.is_superuser" class="header-buttons">
        <el-button type="success" plain @click="showUploadDialog = true">
          엑셀 업로드
        </el-button>
        <el-button type="primary" @click="openCreateDialog">
          신규 등록
        </el-button>
      </div>
    </div>

    <!-- 합계 (기본 숨김, 토글로 표시) -->
    <div class="summary-toggle">
      <el-button text type="primary" @click="toggleSummary">
        {{ showSummary ? '합계 숨기기 ▲' : '합계 보기 ▼' }}
      </el-button>
    </div>
    <CondolenceSummaryCard v-if="showSummary" :summary="summary" :loading="summaryLoading" />

    <!-- 탭: 낸 부조금 / 받은 부조금 / 전체 -->
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <el-tab-pane label="전체" name="ALL" />
      <el-tab-pane label="낸 부조금" name="GIVEN" />
      <el-tab-pane label="받은 부조금" name="RECEIVED" />
    </el-tabs>

    <!-- 검색 -->
    <div class="search-bar">
      <el-input
        v-model="filters.search"
        placeholder="이름, 업체명, 이벤트명으로 검색"
        clearable
        :prefix-icon="SearchIcon"
        size="large"
        @keydown.enter.stop.prevent="onSearchEnter"
      />
    </div>

    <!-- 필터 -->
    <div class="filter-bar">
      <div class="filter-row">
        <el-date-picker
          v-model="filters.year"
          type="year"
          placeholder="연도"
          value-format="YYYY"
          clearable
          size="default"
          :style="{ width: isMobile ? '90px' : '110px' }"
        />
        <el-select v-model="filters.event_type" clearable placeholder="경조사" size="default" :style="{ width: isMobile ? '85px' : '110px' }">
          <el-option label="결혼" value="WEDDING" />
          <el-option label="장례" value="FUNERAL" />
          <el-option label="출산" value="BIRTH" />
          <el-option label="돌잔치" value="BIRTHDAY" />
          <el-option label="기타" value="OTHER" />
        </el-select>
        <el-select v-model="filters.record_type" clearable placeholder="유형" size="default" :style="{ width: isMobile ? '80px' : '100px' }">
          <el-option label="부조금" value="MONEY" />
          <el-option label="화환" value="FLOWER" />
        </el-select>
        <el-button @click="resetFilters" size="default">초기화</el-button>
      </div>
    </div>

    <!-- 결과 영역 -->
    <el-card shadow="never">
      <!-- 검색 전: 안내 (개인정보 보호 — 검색해야 결과 표시) -->
      <el-empty
        v-if="!filters.search"
        :image-size="80"
        description="이름·업체명·이벤트명으로 검색하면 결과가 표시됩니다."
      />

      <template v-else>
        <!-- 테이블 (PC) — 금액은 목록 비공개, 클릭(상세)에서만 -->
        <el-table
          v-if="!isMobile"
          :data="records"
          v-loading="loading"
          stripe
          highlight-current-row
          @row-click="openDetail"
          style="cursor: pointer"
        >
          <el-table-column prop="event_date" label="날짜" width="110" />
          <el-table-column label="경조사" width="80">
            <template #default="{ row }">
              <el-tag :type="getEventTagType(row.event_type)" size="small">
                {{ row.event_type_display }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="person_name" label="이름" width="110" />
          <el-table-column prop="company_name" label="업체명" width="140" show-overflow-tooltip />
          <el-table-column prop="event_name" label="이벤트명" min-width="150" show-overflow-tooltip />
          <el-table-column label="구분" width="80" v-if="activeTab === 'ALL'">
            <template #default="{ row }">
              <el-tag :type="row.direction === 'GIVEN' ? 'danger' : 'success'" size="small">
                {{ row.direction_display }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="유형" width="80">
            <template #default="{ row }">
              <el-tag :type="row.record_type === 'FLOWER' ? 'warning' : ''" size="small">
                {{ row.record_type_display }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="금액" width="120" align="right">
            <template #default="{ row }">
              <span class="amount-value">{{ formatNumber(row.amount) }}원</span>
            </template>
          </el-table-column>
          <el-table-column prop="memo" label="비고" min-width="100" show-overflow-tooltip />
        </el-table>

        <!-- 카드 리스트 (모바일) — 사람 강조, 금액 비공개 -->
        <div v-else class="mobile-card-list" v-loading="loading">
          <div
            v-for="record in records"
            :key="record.id"
            class="record-card"
            @click="openDetail(record)"
          >
            <div class="record-header">
              <el-tag :type="getEventTagType(record.event_type)" size="small">
                {{ record.event_type_display }}
              </el-tag>
              <el-tag :type="record.direction === 'GIVEN' ? 'danger' : 'success'" size="small">
                {{ record.direction_display }}
              </el-tag>
              <span class="record-date">{{ record.event_date }}</span>
            </div>
            <div class="record-person">
              {{ record.person_name }}
              <span v-if="record.company_name" class="record-company">/ {{ record.company_name }}</span>
            </div>
            <div class="record-event">{{ record.event_name }}</div>
            <div class="record-meta">
              <el-tag :type="record.record_type === 'FLOWER' ? 'warning' : ''" size="small">
                {{ record.record_type_display }}
              </el-tag>
              <span class="amount-value">{{ formatNumber(record.amount) }}원</span>
            </div>
            <div v-if="record.memo" class="record-memo">{{ record.memo }}</div>
          </div>
          <el-empty v-if="!loading && records.length === 0" description="검색 결과가 없습니다" />
        </div>

        <!-- 페이지네이션 -->
        <div style="margin-top: 16px; display: flex; justify-content: center">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[20, 50, 100]"
            :total="pagination.total"
            :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next'"
            @size-change="fetchData"
            @current-change="fetchData"
          />
        </div>
      </template>
    </el-card>

    <!-- 상세보기 (읽기전용) — 수정 버튼을 눌러야만 수정/삭제 화면으로 이동 -->
    <el-dialog
      v-model="showDetailDialog"
      title="부조금 상세"
      :width="isMobile ? undefined : '480px'"
      :fullscreen="isMobile"
    >
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="이 상세 정보 열람 내역은 감사 로그에 기록됩니다."
        style="margin-bottom: 12px"
      />
      <div v-if="detailRecord" class="detail-body">
        <div class="detail-tags">
          <el-tag :type="getEventTagType(detailRecord.event_type)" size="small">{{ detailRecord.event_type_display }}</el-tag>
          <el-tag :type="detailRecord.direction === 'GIVEN' ? 'danger' : 'success'" size="small">{{ detailRecord.direction_display }}</el-tag>
          <el-tag :type="detailRecord.record_type === 'FLOWER' ? 'warning' : ''" size="small">{{ detailRecord.record_type_display }}</el-tag>
        </div>
        <div class="detail-row"><span class="detail-label">이름</span><span class="detail-value strong">{{ detailRecord.person_name }}</span></div>
        <div class="detail-row" v-if="detailRecord.company_name"><span class="detail-label">업체명</span><span class="detail-value">{{ detailRecord.company_name }}</span></div>
        <div class="detail-row" v-if="detailRecord.relationship"><span class="detail-label">관계</span><span class="detail-value">{{ detailRecord.relationship }}</span></div>
        <div class="detail-row"><span class="detail-label">이벤트명</span><span class="detail-value">{{ detailRecord.event_name }}</span></div>
        <div class="detail-row"><span class="detail-label">경조사일</span><span class="detail-value">{{ detailRecord.event_date }}</span></div>
        <div class="detail-row" v-if="detailRecord.record_type === 'FLOWER' && detailRecord.flower_description"><span class="detail-label">화환종류</span><span class="detail-value">{{ detailRecord.flower_description }}</span></div>
        <div class="detail-row"><span class="detail-label">금액</span><span class="detail-value strong amount">{{ formatNumber(detailRecord.amount) }}원</span></div>
        <div class="detail-row" v-if="detailRecord.memo"><span class="detail-label">비고</span><span class="detail-value">{{ detailRecord.memo }}</span></div>
      </div>
      <template #footer>
        <div class="detail-footer">
          <el-button v-if="canManage" text type="primary" size="small" @click="editFromDetail">
            수정하기
          </el-button>
          <el-button type="primary" @click="showDetailDialog = false">닫기</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 생성/수정 다이얼로그 -->
    <CondolenceFormDialog
      v-model="showFormDialog"
      :record="selectedRecord"
      :default-direction="activeTab !== 'ALL' ? activeTab : 'GIVEN'"
      @saved="handleSaved"
    />

    <!-- 엑셀 업로드 다이얼로그 -->
    <CondolenceUploadDialog
      v-model="showUploadDialog"
      @uploaded="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted, onUnmounted } from 'vue'
import axiosInstance from '@/api/axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import CondolenceFormDialog from '@/components/condolence/CondolenceFormDialog.vue'
import CondolenceUploadDialog from '@/components/condolence/CondolenceUploadDialog.vue'
import CondolenceSummaryCard from '@/components/condolence/CondolenceSummaryCard.vue'
import { Search as SearchIcon } from '@element-plus/icons-vue'

const userStore = useUserStore()
const canManage = computed(() => userStore.isCondolenceManager || userStore.profile?.is_superuser)

// 반응형
const isMobile = ref(window.innerWidth <= 768)
const updateIsMobile = () => { isMobile.value = window.innerWidth <= 768 }
onMounted(() => window.addEventListener('resize', updateIsMobile))
onUnmounted(() => window.removeEventListener('resize', updateIsMobile))

// 상태
const loading = ref(false)
const summaryLoading = ref(false)
const showSummary = ref(false)       // 합계 기본 숨김
const summaryLoaded = ref(false)     // 합계 최초 토글 시에만 로드
const records = ref([])
const activeTab = ref('ALL')
const showFormDialog = ref(false)
const showDetailDialog = ref(false)
const showUploadDialog = ref(false)
const selectedRecord = ref(null)
const detailRecord = ref(null)

const filters = reactive({
  year: '',  // 기본 전체 연도 — 검색 시 모든 연도 대상으로 조회 (연도 선택 시에만 좁힘)
  event_type: '',
  record_type: '',
  search: '',
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
})

const summary = ref({
  given_money: { total: 0, count: 0 },
  received_money: { total: 0, count: 0 },
  given_flower: { total: 0, count: 0 },
  received_flower: { total: 0, count: 0 },
})

// 데이터 조회
const fetchData = async () => {
  // 개인정보 보호: 검색어가 없으면 목록을 조회하지 않음 (검색 후에만 결과 표시)
  if (!filters.search || !filters.search.trim()) {
    records.value = []
    pagination.total = 0
    return
  }
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
    }

    if (activeTab.value !== 'ALL') params.direction = activeTab.value
    if (filters.year) params.year = filters.year
    if (filters.event_type) params.event_type = filters.event_type
    if (filters.record_type) params.record_type = filters.record_type
    if (filters.search) params.search = filters.search

    const response = await axiosInstance.get('/condolences/', { params })
    records.value = response.data.results || response.data
    pagination.total = response.data.count || records.value.length
  } catch (error) {
    console.error('Failed to fetch condolences:', error)
  } finally {
    loading.value = false
  }
}

// 요약 통계 조회
const fetchSummary = async () => {
  summaryLoading.value = true
  try {
    const params = {}
    if (filters.year) params.year = filters.year

    const response = await axiosInstance.get('/condolences/summary/', { params })
    summary.value = response.data
  } catch (error) {
    console.error('Failed to fetch summary:', error)
  } finally {
    summaryLoading.value = false
  }
}

// 합계 토글 (최초 펼칠 때만 로드)
const toggleSummary = () => {
  showSummary.value = !showSummary.value
  if (showSummary.value && !summaryLoaded.value) {
    fetchSummary()
    summaryLoaded.value = true
  }
}

// 디바운스 헬퍼
let searchTimer = null
let skipWatch = false

const debouncedFetch = () => {
  if (skipWatch) return
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    pagination.page = 1
    fetchData()
  }, 300)
}

// 검색창에서 엔터 → 즉시 조회. 전파 차단 + 포커스 해제로 연도칸 이동 방지
const onSearchEnter = (e) => {
  const el = e && e.target
  if (el && typeof el.blur === 'function') el.blur()  // 검색창 포커스 제거 → 다른 칸으로 안 넘어감
  clearTimeout(searchTimer)
  pagination.page = 1
  fetchData()
}

// 검색어 실시간 반영 (타이핑할 때마다 300ms 디바운스)
watch(() => filters.search, debouncedFetch)

// 필터 변경 시 즉시 조회
watch(() => filters.event_type, () => { if (!skipWatch) { pagination.page = 1; fetchData() } })
watch(() => filters.record_type, () => { if (!skipWatch) { pagination.page = 1; fetchData() } })
watch(() => filters.year, () => { if (!skipWatch) { pagination.page = 1; fetchData(); if (showSummary.value) fetchSummary() } })

// 탭 변경
const handleTabChange = () => {
  pagination.page = 1
  fetchData()
}

// 필터 초기화
const resetFilters = () => {
  skipWatch = true
  filters.year = ''
  filters.event_type = ''
  filters.record_type = ''
  filters.search = ''
  pagination.page = 1
  skipWatch = false
  fetchData()
  if (showSummary.value) fetchSummary()
}

// 다이얼로그
const openCreateDialog = () => {
  selectedRecord.value = null
  showFormDialog.value = true
}

// 행 클릭 → 읽기전용 상세보기 (실수 수정/삭제 방지). 상세 조회 시 백엔드가 감사로그 기록.
const openDetail = async (row) => {
  try {
    const res = await axiosInstance.get(`/condolences/${row.id}/`)
    detailRecord.value = { ...res.data }
    showDetailDialog.value = true
  } catch (error) {
    ElMessage.error('상세 정보를 불러오지 못했습니다.')
    console.error('Failed to fetch condolence detail:', error)
  }
}

// 상세 → 수정/삭제 가능한 폼으로 이동 (수정 버튼을 눌러야만)
const editFromDetail = () => {
  selectedRecord.value = { ...detailRecord.value }
  showDetailDialog.value = false
  showFormDialog.value = true
}

const handleSaved = () => {
  fetchData()
  if (showSummary.value) fetchSummary()
}

const formatNumber = (num) => {
  if (!num) return '0'
  return Number(num).toLocaleString()
}

const getEventTagType = (type) => {
  const map = {
    WEDDING: '',
    FUNERAL: 'info',
    BIRTH: 'success',
    BIRTHDAY: 'warning',
    OTHER: 'info',
  }
  return map[type] || 'info'
}

// 초기 로딩 안 함 (개인정보 보호): 목록은 검색 시, 합계는 토글 시에만 로드
</script>

<style scoped>
.condolence-view {
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
}

/* 검색바 */
.search-bar {
  margin-bottom: 10px;
}

/* 필터 */
.filter-bar {
  margin-bottom: 10px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* 모바일 카드 리스트 */
.mobile-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.record-card:active {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.record-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.record-date {
  margin-left: auto;
  color: #909399;
  font-size: 13px;
}

.record-title {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
}

.record-detail {
  color: #606266;
  font-size: 13px;
  margin-bottom: 6px;
}

.record-amount {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.record-amount strong {
  font-size: 16px;
  color: #303133;
}

.record-memo {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
  color: #909399;
  font-size: 12px;
}

/* 합계 토글 */
.summary-toggle {
  margin-bottom: 8px;
}

/* 모바일 카드 — 사람 강조 / 이벤트 보조 / 금액 비공개 */
.record-person {
  font-weight: 700;
  font-size: 16px;
  color: #303133;
  margin-bottom: 2px;
}

.record-company {
  font-weight: 400;
  font-size: 13px;
  color: #909399;
}

.record-event {
  font-size: 13px;
  color: #909399;
  margin-bottom: 6px;
}

.record-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.amount-masked {
  color: #c0c4cc;
  font-size: 12px;
}

.amount-value {
  color: #303133;
  font-weight: 600;
  font-size: 14px;
}

/* 상세보기 다이얼로그 */
.detail-tags {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.detail-row {
  display: flex;
  padding: 7px 0;
  border-bottom: 1px solid #f5f7fa;
}

.detail-label {
  width: 80px;
  flex-shrink: 0;
  color: #909399;
  font-size: 13px;
}

.detail-value {
  color: #303133;
  font-size: 14px;
}

.detail-value.strong {
  font-weight: 700;
}

.detail-value.amount {
  color: #409eff;
}

/* 상세 푸터: 닫기(주버튼, 우측) / 수정(작은 텍스트, 좌측) 분리해 오탭 방지 */
.detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .page-header h2 {
    font-size: 17px;
  }

  .filter-row {
    gap: 6px;
  }
}
</style>
